import * as React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

interface FilterChipOption {
  value: string
  label: React.ReactNode
}

interface FilterChipProps {
  label: string
  value: string
  onValueChange: (value: string) => void
  options: FilterChipOption[]
  allLabel: string
  className?: string
  width?: string
}

/**
 * Label-prefixed select rendered as a chip. The trigger looks like
 *   `Status` (muted, small)  `All` (foreground, medium)
 * When a non-"all" value is selected the chip subtly tints to mark it
 * as an active filter — matches the "Clear filters" affordance.
 */
export function FilterChip({
  label,
  value,
  onValueChange,
  options,
  allLabel,
  width = "auto",
  className,
}: FilterChipProps) {
  const selected = options.find((o) => o.value === value)
  const isActive = value !== "all" && !!selected
  const displayed = isActive ? selected.label : allLabel
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          "h-10 transition-colors",
          isActive
            ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
            : "hover:bg-muted/60",
          className,
        )}
        style={width !== "auto" ? { minWidth: width } : undefined}
      >
        <SelectValue>
          <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {label}
          </span>
          <span
            className={cn(
              "ml-1.5 text-sm font-medium",
              isActive ? "text-primary" : "text-foreground",
            )}
          >
            {displayed}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
